import { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Download, MoreHorizontal, Calendar, Clock, Users, ArrowUpRight, FileAudio, UploadCloud, Trash2, Copy, Share2, X, Check, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/auth'
import { recordingsService } from '@/services/firebase.service'
import { auth } from '@/lib/firebase'

interface Recording {
  id: string
  title: string
  participants: number
  duration: string
  date: string
  status: 'Processed' | 'Processing' | 'Error'
  size: string
  team?: string
}

type StatusFilter = 'all' | 'Processed' | 'Processing' | 'Error'
type DateFilter = 'all' | 'today' | 'week' | 'month'

// Helper functions
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + 'KB'
  return Math.round(bytes / (1024 * 1024)) + 'MB'
}

const formatDuration = (seconds: number) => {
  if (!seconds) return '0m'
  const mins = Math.floor(seconds / 60)
  const hours = Math.floor(mins / 60)
  if (hours > 0) return `${hours}h ${mins % 60}m`
  return `${mins}m`
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return `Oggi, ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
  if (diffDays === 1) return `Ieri, ${date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
  if (diffDays < 7) return `${diffDays} giorni fa`
  return date.toLocaleDateString('it-IT')
}

export function Recordings() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [_isLoading, setIsLoading] = useState(true)

  // Load recordings from Firestore
  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid
    if (!user || !firebaseUid) return

    setIsLoading(true)
    const unsubscribe = recordingsService.subscribeAll(firebaseUid, (firebaseRecordings) => {
      const formattedRecordings: Recording[] = firebaseRecordings.map(r => ({
        id: r.id,
        title: r.title || r.fileName || 'Registrazione',
        participants: r.speakers?.length || 1,
        duration: formatDuration(r.duration),
        date: formatDate(r.createdAt),
        status: r.status === 'completed' ? 'Processed' : r.status === 'error' ? 'Error' : 'Processing',
        size: formatFileSize(r.fileSize),
        team: undefined,
      }))
      setRecordings(formattedRecordings)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [user])
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [teamFilter, setTeamFilter] = useState<string>('all')
  const [isExporting, setIsExporting] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showDateDropdown, setShowDateDropdown] = useState(false)
  const [showTeamDropdown, setShowTeamDropdown] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const dateRef = useRef<HTMLDivElement>(null)
  const teamRef = useRef<HTMLDivElement>(null)

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null)
      }
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false)
      }
      if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
        setShowDateDropdown(false)
      }
      if (teamRef.current && !teamRef.current.contains(event.target as Node)) {
        setShowTeamDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get unique teams
  const teams = Array.from(new Set(recordings.map(r => r.team).filter(Boolean)))

  // Filter recordings
  const filteredRecordings = recordings.filter(rec => {
    // Search filter
    const matchesSearch = searchQuery === '' ||
      rec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rec.team?.toLowerCase().includes(searchQuery.toLowerCase())

    // Status filter
    const matchesStatus = statusFilter === 'all' || rec.status === statusFilter

    // Team filter
    const matchesTeam = teamFilter === 'all' || rec.team === teamFilter

    // Date filter (simplified simulation)
    let matchesDate = true
    if (dateFilter === 'today') {
      matchesDate = rec.date.includes('Oggi')
    } else if (dateFilter === 'week') {
      matchesDate = rec.date.includes('Oggi') || rec.date.includes('Ieri') || rec.date.includes('2 giorni')
    }
    // 'month' and 'all' show everything

    return matchesSearch && matchesStatus && matchesTeam && matchesDate
  })

  const handleExportCSV = useCallback(() => {
    setIsExporting(true)
    toast.loading('Generazione CSV in corso...', { id: 'export-csv' })

    setTimeout(() => {
      // Simulate CSV generation
      const csvContent = [
        'ID,Titolo,Partecipanti,Durata,Data,Status,Dimensione,Team',
        ...filteredRecordings.map(r =>
          `${r.id},"${r.title}",${r.participants},"${r.duration}","${r.date}",${r.status},${r.size},${r.team || 'N/A'}`
        )
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `nexscribe_recordings_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(url)

      setIsExporting(false)
      toast.success(`${filteredRecordings.length} registrazioni esportate!`, { id: 'export-csv' })
    }, 1500)
  }, [filteredRecordings])

  const openRecording = useCallback((recording: Recording) => {
    if (recording.status === 'Processing') {
      toast('Elaborazione ancora in corso...', { icon: '⏳' })
      return
    }
    if (recording.status === 'Error') {
      toast.error('Errore nel processamento. Riprova il caricamento.')
      return
    }
    navigate(`/recordings/${recording.id}`)
  }, [navigate])

  const handleCopyLink = useCallback((recording: Recording, e: React.MouseEvent) => {
    e.stopPropagation()
    const link = `${window.location.origin}/recordings/${recording.id}`
    navigator.clipboard.writeText(link)
    toast.success('Link copiato negli appunti!')
    setOpenMenuId(null)
  }, [])

  const handleShare = useCallback((recording: Recording, e: React.MouseEvent) => {
    e.stopPropagation()
    toast.success(`Condivisione "${recording.title}" aperta!`)
    setOpenMenuId(null)
  }, [])

  const handleDownload = useCallback((recording: Recording, e: React.MouseEvent) => {
    e.stopPropagation()
    toast.loading(`Download "${recording.title}"...`, { id: `download-${recording.id}` })
    setTimeout(() => {
      toast.success(`"${recording.title}" scaricato!`, { id: `download-${recording.id}` })
    }, 1500)
    setOpenMenuId(null)
  }, [])

  const handleDelete = useCallback(async (recording: Recording, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      toast.loading('Eliminazione...', { id: `delete-${recording.id}` })
      await recordingsService.delete(recording.id)
      toast.success(`"${recording.title}" eliminato da Firebase.`, { id: `delete-${recording.id}` })
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Eliminazione fallita'), { id: `delete-${recording.id}` })
    }
    setOpenMenuId(null)
  }, [])

  const clearFilters = useCallback(() => {
    setSearchQuery('')
    setStatusFilter('all')
    setDateFilter('all')
    setTeamFilter('all')
    toast.success('Filtri reimpostati')
  }, [])

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || dateFilter !== 'all' || teamFilter !== 'all'

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Archivio Intelligence</h2>
          <p className="text-slate-500 mt-1">Database ricercabile di tutte le riunioni aziendali processate.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportCSV}
            disabled={isExporting || filteredRecordings.length === 0}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export CSV
          </button>
          <button
            onClick={() => navigate('/upload')}
            className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg shadow-slate-900/20 transition-all active:scale-95"
          >
            <UploadCloud className="h-4 w-4" />
            Nuovo Upload
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per keyword, speaker o titolo..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border-transparent hover:bg-slate-100 focus:bg-white focus:border-slate-300 rounded-lg text-sm transition-all focus:outline-none focus:ring-0"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {/* Date Filter */}
          <div className="relative" ref={dateRef}>
            <button
              onClick={() => {
                setShowDateDropdown(!showDateDropdown)
                setShowStatusDropdown(false)
                setShowTeamDropdown(false)
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                dateFilter !== 'all'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-transparent hover:border-slate-200'
              }`}
            >
              <Calendar className="h-4 w-4" />
              {dateFilter === 'all' ? 'Data' : dateFilter === 'today' ? 'Oggi' : dateFilter === 'week' ? 'Settimana' : 'Mese'}
            </button>
            {showDateDropdown && (
              <div className="absolute top-full left-0 mt-2 w-40 glass-dropdown z-[200] overflow-hidden">
                {[
                  { value: 'all', label: 'Tutte le date' },
                  { value: 'today', label: 'Oggi' },
                  { value: 'week', label: 'Ultima settimana' },
                  { value: 'month', label: 'Ultimo mese' },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setDateFilter(option.value as DateFilter)
                      setShowDateDropdown(false)
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                      dateFilter === option.value ? 'text-blue-600 bg-blue-50' : 'text-slate-700'
                    }`}
                  >
                    {option.label}
                    {dateFilter === option.value && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Team Filter */}
          <div className="relative" ref={teamRef}>
            <button
              onClick={() => {
                setShowTeamDropdown(!showTeamDropdown)
                setShowStatusDropdown(false)
                setShowDateDropdown(false)
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                teamFilter !== 'all'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-transparent hover:border-slate-200'
              }`}
            >
              <Users className="h-4 w-4" />
              {teamFilter === 'all' ? 'Team' : teamFilter}
            </button>
            {showTeamDropdown && (
              <div className="absolute top-full left-0 mt-2 w-40 glass-dropdown z-[200] overflow-hidden">
                <button
                  onClick={() => {
                    setTeamFilter('all')
                    setShowTeamDropdown(false)
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                    teamFilter === 'all' ? 'text-blue-600 bg-blue-50' : 'text-slate-700'
                  }`}
                >
                  Tutti i team
                  {teamFilter === 'all' && <Check className="h-4 w-4" />}
                </button>
                {teams.map(team => (
                  <button
                    key={team}
                    onClick={() => {
                      setTeamFilter(team!)
                      setShowTeamDropdown(false)
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                      teamFilter === team ? 'text-blue-600 bg-blue-50' : 'text-slate-700'
                    }`}
                  >
                    {team}
                    {teamFilter === team && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative" ref={statusRef}>
            <button
              onClick={() => {
                setShowStatusDropdown(!showStatusDropdown)
                setShowDateDropdown(false)
                setShowTeamDropdown(false)
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                statusFilter !== 'all'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-transparent hover:border-slate-200'
              }`}
            >
              <Filter className="h-4 w-4" />
              {statusFilter === 'all' ? 'Stato' : statusFilter}
            </button>
            {showStatusDropdown && (
              <div className="absolute top-full right-0 mt-2 w-40 glass-dropdown z-[200] overflow-hidden">
                {[
                  { value: 'all', label: 'Tutti gli stati' },
                  { value: 'Processed', label: 'Completati' },
                  { value: 'Processing', label: 'In elaborazione' },
                  { value: 'Error', label: 'Errore' },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setStatusFilter(option.value as StatusFilter)
                      setShowStatusDropdown(false)
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                      statusFilter === option.value ? 'text-blue-600 bg-blue-50' : 'text-slate-700'
                    }`}
                  >
                    {option.label}
                    {statusFilter === option.value && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-slate-500 hover:text-red-600 text-sm font-medium flex items-center gap-1 transition-colors"
            >
              <X className="h-4 w-4" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* Results Count */}
      {hasActiveFilters && (
        <p className="text-sm text-slate-500">
          {filteredRecordings.length} risultat{filteredRecordings.length === 1 ? 'o' : 'i'} trovat{filteredRecordings.length === 1 ? 'o' : 'i'}
        </p>
      )}

      {/* Recordings Table - Enterprise Style */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {filteredRecordings.length === 0 ? (
          <div className="p-12 text-center">
            <FileAudio className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessuna registrazione trovata</h3>
            <p className="text-sm text-slate-500 mb-4">
              {hasActiveFilters
                ? 'Prova a modificare i filtri di ricerca.'
                : 'Carica il tuo primo file audio per iniziare.'}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Rimuovi filtri
              </button>
            ) : (
              <button
                onClick={() => navigate('/upload')}
                className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
              >
                <UploadCloud className="h-4 w-4" /> Carica Audio
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50/80 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-6 py-4">Nome File / Riunione</th>
                <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-6 py-4">Metadati</th>
                <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-6 py-4">Data Ingestione</th>
                <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-6 py-4">Status</th>
                <th className="text-right text-xs font-bold text-slate-500 uppercase tracking-wider px-6 py-4">Opzioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRecordings.map((call) => (
                <tr
                  key={call.id}
                  onClick={() => openRecording(call)}
                  className={`hover:bg-slate-50/80 transition-colors group ${
                    call.status === 'Processed' ? 'cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center transition-all border ${
                        call.status === 'Error'
                          ? 'bg-red-50 text-red-500 border-red-100'
                          : call.status === 'Processing'
                          ? 'bg-amber-50 text-amber-500 border-amber-100'
                          : 'bg-slate-100 text-slate-500 border-transparent group-hover:bg-white group-hover:text-slate-900 group-hover:shadow-md group-hover:border-slate-200'
                      }`}>
                        <FileAudio className="h-5 w-5" />
                      </div>
                      <div>
                        <p className={`font-semibold transition-colors ${
                          call.status === 'Processed'
                            ? 'text-slate-900 group-hover:text-blue-600'
                            : 'text-slate-600'
                        }`}>{call.title}</p>
                        <p className="text-xs text-slate-500 font-mono">{call.size} • MP3 {call.team && `• ${call.team}`}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Clock className="h-3 w-3 text-slate-400" /> {call.duration}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Users className="h-3 w-3 text-slate-400" /> {call.participants} Speakers
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{call.date}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      call.status === 'Processing'
                        ? 'bg-amber-50 text-amber-700 border-amber-100'
                        : call.status === 'Error'
                        ? 'bg-red-50 text-red-700 border-red-100'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}>
                      {call.status === 'Processing' && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5 animate-pulse" />}
                      {call.status === 'Error' && <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5" />}
                      {call.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {call.status === 'Processed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openRecording(call)
                          }}
                          className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"
                          title="Analizza"
                        >
                          <ArrowUpRight className="h-4 w-4" />
                        </button>
                      )}
                      <div className="relative" ref={openMenuId === call.id ? menuRef : null}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenMenuId(openMenuId === call.id ? null : call.id)
                          }}
                          className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                        {openMenuId === call.id && (
                          <div className="absolute top-full right-0 mt-2 w-48 glass-dropdown z-[200] overflow-hidden">
                            <button
                              onClick={(e) => handleCopyLink(call, e)}
                              className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                            >
                              <Copy className="h-4 w-4 text-slate-400" /> Copia link
                            </button>
                            <button
                              onClick={(e) => handleShare(call, e)}
                              className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                            >
                              <Share2 className="h-4 w-4 text-slate-400" /> Condividi
                            </button>
                            <button
                              onClick={(e) => handleDownload(call, e)}
                              className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                            >
                              <Download className="h-4 w-4 text-slate-400" /> Scarica audio
                            </button>
                            <div className="border-t border-slate-100" />
                            <button
                              onClick={(e) => handleDelete(call, e)}
                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                            >
                              <Trash2 className="h-4 w-4" /> Elimina
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
